import React, { useEffect } from 'react';
import { Field as FinalField } from 'react-final-form';
import { cloneDeep, every, get, isArray, isUndefined, omit, set } from 'lodash';
import AddressPicker, { AddressObject, AddressPickerProps } from '@lendi/address-picker';
import Field from '@lendi-ui/field';

import { useDidUpdate, useFormValues, useUpdatedRef } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { Address, TrackingProps } from '@funnels/types';
import { datadogUtil, initialFormValues, validateAddress } from '@funnels/utils';

import { FieldWrapper } from '../../_common/style';
import { FieldLabel } from '../_common/FieldLabel';
import { CommonFieldProps } from '../_common/types';

export interface AddressFieldProps extends CommonFieldProps, Omit<AddressPickerProps, 'onChange' | 'onSelectAddress'> {
  path: string;
  onSelectAddress?: (value: AddressObject, formattedAddress?: string) => void;
  regionSearchOnly?: boolean;
  allowOverseaAddress?: boolean;
  additionalPathsToUpdate?: string[];
  allowPOBox?: boolean;
  trackingProps?: TrackingProps;
}

const AddressField = (props: AddressFieldProps) => {
  const { tracking } = useAnalytics();
  const {
    label,
    labelAfter,
    name,
    path,
    subLabel,
    isDisabled,
    isOptional,
    validationFailMessage,
    disableFailMessage = false,
    onSelectAddress,
    onReset,
    regionSearchOnly,
    className,
    allowOverseaAddress = true,
    additionalPathsToUpdate,
    allowPOBox,
    trackingProps = {},
  } = props;
  const [formValues, setFormValue] = useFormValues();
  const address = get(formValues, path, {} as Address);
  const addressObj = cloneDeep(address);
  const validationRef = useUpdatedRef({ isDisabled, isOptional, addressObj });
  const validate = (value: any, allValues: any) => {
    if (validationRef.current.isDisabled) {
      return;
    }

    return validateAddress(
      validationFailMessage,
      get(allValues, path, addressObj),
      validationRef.current.isOptional,
      allowOverseaAddress,
      allowPOBox,
      regionSearchOnly
    )(value);
  };
  const [addressUndefined, setAddressUndefined] = React.useState<boolean>(false);
  const fieldProps = {
    name,
    validate,
  };

  function handleAddressSelect(value: AddressObject, formattedAddress?: string) {
    set(addressObj, 'postcode', value.postcode || undefined);
    set(addressObj, 'state', value.state || undefined);
    set(addressObj, 'city', value.suburb || undefined);
    set(addressObj, 'country', value.country || 'Australia');
    set(addressObj, 'standard.streetName', value.streetName || undefined);
    set(addressObj, 'standard.streetNumber', value.streetNumber || undefined);
    set(addressObj, 'standard.streetSuffix', value.streetSuffix || undefined);
    set(addressObj, 'standard.streetType', value.streetType || undefined);
    set(addressObj, 'standard.buildingName', value.buildingName || undefined);
    set(addressObj, 'standard.level', value.level || undefined);
    set(addressObj, 'standard.unit', value.unit || undefined);
    set(addressObj, 'standard.lotSection', value.lotSection || undefined);
    set(addressObj, 'standard.deliveryAddress', value.deliveryAddress || undefined);
    set(addressObj, 'formatted', formattedAddress || undefined);
    // addressObj.city - no city export from LUI address picker at the moment.
    // now the address component only be used for setting address on assets/properties.
    tracking?.dropdown({
      title: label || '',
      position: 'Address selected',
      selection: '*PII_Masked*',
      ...trackingProps,
    });
    setFormValue(path, addressObj);
    // update form value for additional paths
    isArray(additionalPathsToUpdate) &&
      additionalPathsToUpdate.forEach((path) => {
        setFormValue(path, addressObj);
      });
    if (onSelectAddress) {
      onSelectAddress(value, formattedAddress);
    }
  }

  function handleChange() {
    if (!addressUndefined) {
      handleReset();
    }
  }

  function handleReset() {
    set(addressObj, 'postcode', undefined);
    set(addressObj, 'state', undefined);
    set(addressObj, 'city', undefined);
    set(addressObj, 'country', 'Australia');
    set(addressObj, 'standard.streetName', undefined);
    set(addressObj, 'standard.streetNumber', undefined);
    set(addressObj, 'standard.streetSuffix', undefined);
    set(addressObj, 'standard.streetType', undefined);
    set(addressObj, 'standard.level', undefined);
    set(addressObj, 'standard.buildingName', undefined);
    set(addressObj, 'standard.unit', undefined);
    set(addressObj, 'standard.lotSection', undefined);
    set(addressObj, 'standard.deliveryAddress', undefined);
    set(addressObj, 'formatted', undefined);

    setFormValue(path, initialFormValues.INITIAL_ADDRESS());
    // reset form value for additional paths
    isArray(additionalPathsToUpdate) &&
      additionalPathsToUpdate.forEach((path) => {
        setFormValue(path, initialFormValues.INITIAL_ADDRESS());
      });
    if (onReset) {
      onReset();
    }
  }

  useEffect(() => {
    setAddressUndefined(every(omit(addressObj, 'standard'), isUndefined) && every(addressObj.standard, isUndefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useDidUpdate(handleReset, [isDisabled]);

  return (
    <FieldWrapper>
      <FieldLabel label={label} labelAfter={labelAfter} subLabel={subLabel} isOptional={isOptional} />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
            <AddressPicker
              {...input}
              className={className}
              data-component={name}
              onChange={handleChange}
              onSelectAddress={handleAddressSelect}
              onReset={handleReset}
              isDisabled={isDisabled}
              isError={!disableFailMessage && !!meta.error && !!meta.touched}
              key={addressObj?.formatted}
              value={!isDisabled ? addressObj.formatted : undefined}
              regionSearchOnly={regionSearchOnly}
              dataset={allowPOBox ? 'PAF' : 'GNAF'}
              manualAddressInputType={allowPOBox ? 'both' : 'residential'}
              onException={(e) => datadogUtil.addError(e)}
            />
          </Field>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default AddressField;
